import React, { useState } from 'react';
import { Save, Edit, Trash2, Search, Plus, X } from 'lucide-react';
import { useContatti } from '../hooks/useSupabase';

const Contatti = () => {
  const { contatti, loading, error, addContatto, updateContatto, deleteContatto } = useContatti();
  
  const [nuovoContatto, setNuovoContatto] = useState({
    id: '',
    nome: '',
    cognome: '',
    azienda: '',
    telefono: '',
    indirizzo: '',
    email: '',
    isCliente: true,
    isVettore: false
  });
  
  const [modalitaModifica, setModalitaModifica] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const aggiungiContatto = async () => {
    // Controllo dei campi obbligatori
    if (!nuovoContatto.azienda) {
      alert('Il campo "Azienda" è obbligatorio');
      return;
    }
    
    try {
      if (modalitaModifica) {
        await updateContatto(nuovoContatto);
        setModalitaModifica(false);
      } else {
        await addContatto(nuovoContatto);
      }
      
      // Reset form
      setNuovoContatto({
        id: '',
        nome: '',
        cognome: '',
        azienda: '',
        telefono: '',
        indirizzo: '',
        email: '',
        isCliente: true,
        isVettore: false
      });
      
      // Nascondi il form dopo il salvataggio
      setShowForm(false);
      // Pulisci eventuali messaggi di errore
      setErrorMessage('');
    } catch (err) {
      alert(`Errore: ${err.message}`);
    }
  };
  
  const eliminaContatto = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo contatto?')) return;
    
    try {
      await deleteContatto(id);
      // Pulisci eventuali messaggi di errore in caso di successo
      setErrorMessage('');
    } catch (err) {
      if (err.message.includes('violates foreign key constraint')) {
        alert('Impossibile eliminare il contatto perché è utilizzato in uno o più viaggi.');
        // Non mostrare l'errore tecnico nella UI
        setErrorMessage('');
      } else {
        alert(`Errore: ${err.message}`);
        setErrorMessage('');
      }
    }
  };
  
  const modificaContatto = (contatto) => {
    setNuovoContatto(contatto);
    setModalitaModifica(true);
    setShowForm(true);
  };
  
  const nuovoContattoClick = () => {
    setNuovoContatto({
      id: '',
      nome: '',
      cognome: '',
      azienda: '',
      telefono: '',
      indirizzo: '',
      email: '',
      isCliente: true,
      isVettore: false
    });
    setModalitaModifica(false);
    setShowForm(true);
  };
  
  // Filtra i contatti in base al termine di ricerca
  const contattoFiltrati = contatti.filter(contatto => {
    const searchLower = searchTerm.toLowerCase();
    return (
      contatto.nome?.toLowerCase().includes(searchLower) ||
      contatto.cognome?.toLowerCase().includes(searchLower) ||
      contatto.azienda?.toLowerCase().includes(searchLower) ||
      contatto.email?.toLowerCase().includes(searchLower) ||
      contatto.telefono?.includes(searchTerm)
    );
  });

  if (loading) return <div className="text-center py-8">Caricamento in corso...</div>;
  
  // Ignoriamo specificamente gli errori di foreign key constraint che abbiamo già gestito
  if (error && !error.includes('violates foreign key constraint')) {
    return <div className="text-center py-8 text-red-600">Errore: {error}</div>;
  }
  
  if (errorMessage) return <div className="text-center py-8 text-red-600">{errorMessage}</div>;

  return (
    <>
      {/* Container per la riga con entrambi i box */}
      <div className="flex gap-6 mb-6">
        {/* Pulsante Nuovo Contatto - Box Separato */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Gestione Contatti</h2>
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded inline-flex items-center"
            onClick={nuovoContattoClick}
          >
            <Plus size={20} className="mr-2" />
            Nuovo Contatto
          </button>
        </div>
        
        {/* Barra di ricerca - Box Separato */}
        <div className="bg-white p-6 rounded-lg shadow-md flex-grow">
          <h2 className="text-xl font-semibold mb-4">Cerca Contatti</h2>
          <div className="flex items-center relative w-full">
            <Search size={20} className="absolute left-3 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca contatti..."
              className="pl-10 p-2 border rounded w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-3 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Form Anagrafica Contatti (mostrato solo quando necessario) */}
      {showForm && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">{modalitaModifica ? 'Modifica Contatto' : 'Nuovo Contatto'}</h2>
            <button 
              onClick={() => setShowForm(false)}
              className="text-gray-500 hover:text-gray-700"
              title="Chiudi"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 mb-1">Nome</label>
              <input 
                type="text" 
                className="w-full p-2 border rounded"
                value={nuovoContatto.nome}
                onChange={e => setNuovoContatto({...nuovoContatto, nome: e.target.value})}
              />
            </div>
            
            <div>
              <label className="block text-gray-700 mb-1">Cognome</label>
              <input 
                type="text" 
                className="w-full p-2 border rounded"
                value={nuovoContatto.cognome}
                onChange={e => setNuovoContatto({...nuovoContatto, cognome: e.target.value})}
              />
            </div>
            
            <div>
              <label className="block text-gray-700 mb-1">Azienda *</label>
              <input 
                type="text" 
                className="w-full p-2 border rounded"
                value={nuovoContatto.azienda}
                onChange={e => setNuovoContatto({...nuovoContatto, azienda: e.target.value})}
                required
              />
            </div>
            
            <div>
              <label className="block text-gray-700 mb-1">Telefono</label>
              <input 
                type="text" 
                className="w-full p-2 border rounded"
                value={nuovoContatto.telefono}
                onChange={e => setNuovoContatto({...nuovoContatto, telefono: e.target.value})}
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-gray-700 mb-1">Indirizzo</label>
              <input 
                type="text" 
                className="w-full p-2 border rounded"
                value={nuovoContatto.indirizzo}
                onChange={e => setNuovoContatto({...nuovoContatto, indirizzo: e.target.value})}
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-gray-700 mb-1">Email</label>
              <input 
                type="email" 
                className="w-full p-2 border rounded"
                value={nuovoContatto.email}
                onChange={e => setNuovoContatto({...nuovoContatto, email: e.target.value})}
              />
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-gray-700 mb-2">Tipo di contatto</label>
              <div className="flex gap-4">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    className="form-checkbox h-5 w-5 text-blue-600"
                    checked={nuovoContatto.isCliente}
                    onChange={e => setNuovoContatto({...nuovoContatto, isCliente: e.target.checked})}
                  />
                  <span className="ml-2">Cliente</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    className="form-checkbox h-5 w-5 text-blue-600"
                    checked={nuovoContatto.isVettore}
                    onChange={e => setNuovoContatto({...nuovoContatto, isVettore: e.target.checked})}
                  />
                  <span className="ml-2">Vettore</span>
                </label>
              </div>
            </div>
          </div>
          
          <button 
            className="bg-blue-600 text-white px-4 py-2 rounded inline-flex items-center"
            onClick={aggiungiContatto}
          >
            <Save size={20} className="mr-2" />
            {modalitaModifica ? 'Aggiorna Contatto' : 'Salva Contatto'}
          </button>
          
          {modalitaModifica && (
            <button 
              className="bg-gray-500 text-white px-4 py-2 rounded ml-2 inline-flex items-center"
              onClick={() => {
                setModalitaModifica(false);
                setNuovoContatto({
                  id: '',
                  nome: '',
                  cognome: '',
                  azienda: '',
                  telefono: '',
                  indirizzo: '',
                  email: '',
                  isCliente: true,
                  isVettore: false
                });
              }}
            >
              Annulla
            </button>
          )}
        </div>
      )}
      
      {/* Lista Contatti */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">Lista Contatti</h2>
        
        {contattoFiltrati.length === 0 ? (
          <p className="text-gray-500">
            {searchTerm ? 'Nessun contatto trovato per la ricerca effettuata' : 'Nessun contatto registrato'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-3 text-left">Azienda</th>
                  <th className="py-2 px-3 text-left">Nome</th>
                  <th className="py-2 px-3 text-left">Cognome</th>
                  <th className="py-2 px-3 text-left">Telefono</th>
                  <th className="py-2 px-3 text-left">Email</th>
                  <th className="py-2 px-3 text-center">Tipo</th>
                  <th className="py-2 px-3 text-center">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {contattoFiltrati.map(contatto => (
                  <tr key={contatto.id} className="border-t hover:bg-gray-50">
                    <td className="py-2 px-3">{contatto.azienda}</td>
                    <td className="py-2 px-3">{contatto.nome}</td>
                    <td className="py-2 px-3">{contatto.cognome}</td>
                    <td className="py-2 px-3">{contatto.telefono}</td>
                    <td className="py-2 px-3">{contatto.email}</td>
                    <td className="py-2 px-3 text-center">
                      {contatto.isCliente && contatto.isVettore ? "Cliente/Vettore" : 
                      contatto.isCliente ? "Cliente" : 
                      contatto.isVettore ? "Vettore" : ""}
                    </td>
                    <td className="py-2 px-3 text-center">
                      <button 
                        className="text-blue-600 hover:text-blue-800 mr-2"
                        onClick={() => modificaContatto(contatto)}
                        title="Modifica"
                      >
                        <Edit size={18} />
                      </button>
                      <button 
                        className="text-red-600 hover:text-red-800"
                        onClick={() => eliminaContatto(contatto.id)}
                        title="Elimina"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default Contatti;